import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ReadOnlyConfigCard from '../../../shared/ReadOnlyConfigCard';
import CovidScreeningToggle from '../../../entities/covidScreening/CovidScreeningToggle';
import SignalLogArchivingToggle from '../../../entities/hospitals/SignalLogArchivingToggle';
import { HospitalFormValue } from '../types';

interface AdvancedTabProps {
  value: HospitalFormValue;
  onChange: (value: HospitalFormValue) => void;
  disabled?: boolean;
  onSaveConfig?: (configKey: string, config: any) => Promise<void>;
}

const safeParse = (json?: string | null): any | null => {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const formatList = (arr?: any[]): string => (arr && arr.length > 0 ? arr.join(', ') : 'None');

const AdvancedTab: React.FC<AdvancedTabProps> = ({ value }) => {
  const v = value as any;

  const sensorData = safeParse(v.sensorConfigData);
  const feedbackData = safeParse(v.feedbackConfigData);
  const schedNotifData = safeParse(v.scheduleNotificationConfigData);
  const trackingData = safeParse(v.trackingConfigData);
  const webhookData = safeParse(v.webhookConfigData);
  const fhirData = safeParse(v.fhirConsumerConfigData);
  const analyticsData = safeParse(v.analyticsPublisherConfigData);
  const sqsData = safeParse(v.sqsCsvUploadConfigData);
  const twoFaData = safeParse(v.twoFactorAuthConfigData);

  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        Integration and advanced configuration status. These settings are managed through backend metadata and reflect
        the current state of each subsystem for this organization.
      </Typography>

      {/* Operational toggles — these trigger mutations directly */}
      <Box mt={2} mb={3}>
        <Typography variant="overline" color="textSecondary" gutterBottom>
          Operational Controls
        </Typography>
        <Box mt={1} display="flex" style={{ gap: '2rem' }}>
          <CovidScreeningToggle data={v} />
          <SignalLogArchivingToggle data={v} />
        </Box>
      </Box>

      <Divider />

      <Box mt={2}>
        <ReadOnlyConfigCard
          title="Sensor Configuration"
          configured={v.sensorConfigured || false}
          details={
            !v.sensorConfigured
              ? 'Global sensor config (temperature thresholds, RSSI floors, alert ranges) has not been set up.'
              : undefined
          }
          configDetails={
            sensorData
              ? [
                  {
                    label: 'Temperature',
                    value: `${sensorData.temperatureMin ?? '?'}–${sensorData.temperatureMax ?? '?'} ${sensorData.temperatureUnit ?? ''}`,
                  },
                  { label: 'Humidity', value: `${sensorData.humidityMin ?? '?'}–${sensorData.humidityMax ?? '?'}%` },
                  { label: 'Report interval', value: `${sensorData.reportInterval ?? '?'} min` },
                  { label: 'Recipients', value: formatList(sensorData.reportRecipients) },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="Feedback Configuration"
          configured={v.feedbackConfigured || false}
          details={!v.feedbackConfigured ? 'Post-visit patient feedback surveys are not configured.' : undefined}
          configDetails={
            feedbackData
              ? [
                  { label: 'Questions', value: `${feedbackData.questions?.length ?? 0} configured` },
                  { label: 'Rating scale', value: `1–${feedbackData.ratingScale ?? 5}` },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="Schedule Notifications"
          configured={v.scheduleNotificationConfigured || false}
          details={
            !v.scheduleNotificationConfigured ? 'Schedule change email notifications are not set up.' : undefined
          }
          configDetails={
            schedNotifData
              ? [
                  { label: 'Recipients', value: formatList(schedNotifData.notificationRecipients) },
                  {
                    label: 'Triggers',
                    value:
                      [
                        schedNotifData.notifyOnNewProcedure && 'New',
                        schedNotifData.notifyOnCancellation && 'Cancel',
                        schedNotifData.notifyOnReschedule && 'Reschedule',
                      ]
                        .filter(Boolean)
                        .join(', ') || 'None',
                  },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="Tracking Configuration"
          configured={v.trackingConfigured || false}
          details={
            !v.trackingConfigured ? 'Real-time location tracking parameters have not been configured.' : undefined
          }
          configDetails={
            trackingData
              ? [
                  { label: 'Sliding window', value: `${trackingData.staffSwPeriod ?? '?'}s` },
                  { label: 'Min samples', value: `${trackingData.staffMinimumSamples ?? '?'}` },
                  { label: 'Announce change', value: `${trackingData.timeToAnnounceChange ?? '?'}s` },
                  { label: 'Offsite timeout', value: `${trackingData.timeToAnnounceOffsite ?? '?'}s` },
                  { label: 'Dwell threshold', value: `${trackingData.dwellTimeThreshold ?? '?'}s` },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="Webhook"
          configured={v.webhookConfigured || false}
          details={!v.webhookConfigured ? 'No outbound webhook configured.' : undefined}
          configDetails={
            webhookData
              ? [
                  { label: 'URL', value: webhookData.url ?? 'Not set' },
                  { label: 'Events', value: formatList(webhookData.events) },
                  { label: 'Enabled', value: webhookData.enabled ? 'Yes' : 'No' },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="FHIR Consumer"
          configured={v.fhirConsumerConfigured || false}
          details={!v.fhirConsumerConfigured ? 'FHIR consumer not configured.' : undefined}
          configDetails={
            fhirData
              ? [
                  { label: 'Endpoint', value: fhirData.endpoint ?? 'Not set' },
                  { label: 'Client ID', value: fhirData.clientId ?? 'Not set' },
                  { label: 'Resources', value: formatList(fhirData.resourceTypes) },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="Analytics Publisher"
          configured={v.analyticsPublisherConfigured || false}
          details={!v.analyticsPublisherConfigured ? 'Analytics publishing not configured.' : undefined}
          configDetails={
            analyticsData
              ? [
                  { label: 'Recipients', value: formatList(analyticsData.recipients) },
                  { label: 'S3 bucket', value: analyticsData.s3Bucket ?? 'Not set' },
                  { label: 'Frequency', value: analyticsData.frequency ?? 'Not set' },
                  { label: 'Format', value: analyticsData.format ?? 'Not set' },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="SQS CSV Upload"
          configured={v.sqsCsvUploadConfigured || false}
          details={!v.sqsCsvUploadConfigured ? 'SQS CSV upload not configured.' : undefined}
          configDetails={
            sqsData
              ? [
                  { label: 'Queue URL', value: sqsData.queueUrl ?? 'Not set' },
                  { label: 'Region', value: sqsData.region ?? 'Not set' },
                  { label: 'Parser', value: sqsData.parser ?? 'Not set' },
                ]
              : undefined
          }
        />
        <ReadOnlyConfigCard
          title="Two-Factor Authentication"
          configured={v.twoFactorAuthEnabled || false}
          details={!v.twoFactorAuthEnabled ? '2FA is not enabled.' : undefined}
          configDetails={
            twoFaData
              ? [
                  { label: 'Method', value: twoFaData.method ?? 'Not set' },
                  { label: 'Enforce for all', value: twoFaData.enforceForAllUsers ? 'Yes' : 'No' },
                ]
              : undefined
          }
        />
      </Box>

      <ReadOnlyConfigCard
        title="Branding"
        configured={!!v.branding?.careAppLogo}
        configDetails={
          v.branding ? [{ label: 'Care app logo', value: v.branding.careAppLogo || 'Not set' }] : undefined
        }
        details={!v.branding ? 'No branding configured.' : undefined}
      />
    </Box>
  );
};

export default AdvancedTab;
